import React, { useCallback, useEffect, useMemo } from "react";
import { Tabs } from "antd";
import { useSearchParams } from "react-router-dom";

import { FUEL_PRICE_WORKBENCH_TABS, FUEL_PRICE_WORKBENCH_TAB_QUERY_KEY, PRICE_SNAPSHOT_ROLE } from "./constants";
import FuelPriceWorkbenchHeader from "./components/workbench/FuelPriceWorkbenchHeader";
import MarketReferenceWorkbenchPanel from "./components/workbench/MarketReferenceWorkbenchPanel";
import OperationLogsTable from "./components/workbench/OperationLogsTable";
import PricePeriodWorkbenchPanel from "./components/workbench/PricePeriodWorkbenchPanel";
import FuelPriceWorkbenchOverlays from "./components/overlays/FuelPriceWorkbenchOverlays";
import useFuelPriceWorkbenchData from "./hooks/useFuelPriceWorkbenchData";
import useFuelPriceWorkbenchViewState from "./hooks/useFuelPriceWorkbenchViewState";
import { getPriceInputModalConfig } from "./services/fuelPriceWorkbenchViewModel";

const VALID_WORKBENCH_TABS = new Set(Object.values(FUEL_PRICE_WORKBENCH_TABS));

function normalizeWorkbenchTab(tabKey) {
    if (VALID_WORKBENCH_TABS.has(tabKey)) {
        return tabKey;
    }

    return FUEL_PRICE_WORKBENCH_TABS.WORKBENCH;
}

const FuelPriceWorkbenchPage = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const currentTabQueryValue = searchParams.get(FUEL_PRICE_WORKBENCH_TAB_QUERY_KEY);
    const requestedTab = normalizeWorkbenchTab(currentTabQueryValue);
    const data = useFuelPriceWorkbenchData();
    const viewState = useFuelPriceWorkbenchViewState(requestedTab);

    const {
        periods,
        loading,
        pagination,
        filters,
        setFilters,
        vendors,
        stats,
        statusOptions,
        searchPeriods,
        resetFilters,
        handleTableChange,
        createPricePeriod,
        createLoading,
        updateOurPrice,
        updateSupplierQuote,
        priceInputLoading,
        confirmPricePeriod,
        disputePricePeriod,
        disputeLoading,
        deletePricePeriod,
        logs,
        logsLoading,
        logsPagination,
        loadWorkbenchLogs,
        detailPeriod,
        detailLoading,
        detailLogs,
        detailLogsLoading,
        loadDetailWorkspace,
        clearDetailWorkspace,
        getPeriodById,
    } = data;

    const {
        activeTab,
        setActiveTab,
        createModalOpen,
        openCreateModal,
        closeCreateModal,
        priceInputState,
        openPriceInput,
        closePriceInput,
        disputeState,
        openDispute,
        closeDispute,
        comparisonState,
        openComparison,
        closeComparison,
        detailState,
        openDetail,
        closeDetail,
    } = viewState;

    useEffect(() => {
        if (activeTab !== requestedTab) {
            setActiveTab(requestedTab);
        }
    }, [activeTab, requestedTab, setActiveTab]);

    useEffect(() => {
        const canonicalTabQueryValue = requestedTab === FUEL_PRICE_WORKBENCH_TABS.WORKBENCH
            ? null
            : requestedTab;

        if (currentTabQueryValue === canonicalTabQueryValue || (!currentTabQueryValue && !canonicalTabQueryValue)) {
            return;
        }

        const nextSearchParams = new URLSearchParams(searchParams);

        if (canonicalTabQueryValue) {
            nextSearchParams.set(FUEL_PRICE_WORKBENCH_TAB_QUERY_KEY, canonicalTabQueryValue);
        } else {
            nextSearchParams.delete(FUEL_PRICE_WORKBENCH_TAB_QUERY_KEY);
        }

        setSearchParams(nextSearchParams, { replace: true });
    }, [currentTabQueryValue, requestedTab, searchParams, setSearchParams]);

    useEffect(() => {
        if (activeTab === FUEL_PRICE_WORKBENCH_TABS.LOGS) {
            loadWorkbenchLogs(1);
        }
    }, [activeTab, loadWorkbenchLogs]);

    const selectedPriceInputPeriod = getPeriodById(priceInputState.periodId);
    const selectedComparisonPeriod = getPeriodById(comparisonState.periodId);
    const selectedDisputePeriod = getPeriodById(disputeState.periodId);

    const priceInputConfig = useMemo(
        () => getPriceInputModalConfig(priceInputState.snapshotRole),
        [priceInputState.snapshotRole]
    );

    const handleFilterChange = useCallback((patch) => {
        setFilters((current) => ({ ...current, ...patch }));
    }, [setFilters]);

    const handleOpenOurPrice = useCallback((period) => {
        openPriceInput(PRICE_SNAPSHOT_ROLE.INTERNAL_REFERENCE, period.id);
    }, [openPriceInput]);

    const handleOpenSupplierQuote = useCallback((period) => {
        openPriceInput(PRICE_SNAPSHOT_ROLE.SUPPLIER_QUOTE, period.id);
    }, [openPriceInput]);

    const handleSubmitPriceInput = useCallback(async (price) => {
        const periodId = priceInputState.periodId;

        if (!periodId) {
            return;
        }

        const succeeded =
            priceInputState.snapshotRole === PRICE_SNAPSHOT_ROLE.SUPPLIER_QUOTE
                ? await updateSupplierQuote(periodId, price)
                : await updateOurPrice(periodId, price);

        if (succeeded) {
            closePriceInput();
        }
    }, [closePriceInput, priceInputState.periodId, priceInputState.snapshotRole, updateOurPrice, updateSupplierQuote]);

    const handleOpenDispute = useCallback((period) => {
        openDispute(period.id);
    }, [openDispute]);

    const handleSubmitDispute = useCallback(async (reason) => {
        const periodId = disputeState.periodId;

        if (!periodId) {
            return;
        }

        const succeeded = await disputePricePeriod(periodId, reason);

        if (succeeded) {
            closeDispute();
        }
    }, [closeDispute, disputePricePeriod, disputeState.periodId]);

    const handleOpenComparison = useCallback((period) => {
        openComparison(period.id);
    }, [openComparison]);

    const handleViewDetail = useCallback(async (period) => {
        openDetail(period.id);
        await loadDetailWorkspace(period.id);
    }, [loadDetailWorkspace, openDetail]);

    const handleCloseDetail = useCallback(() => {
        closeDetail();
        clearDetailWorkspace();
    }, [clearDetailWorkspace, closeDetail]);

    const handleCreate = useCallback(async (payload) => {
        const succeeded = await createPricePeriod(payload);

        if (succeeded) {
            closeCreateModal();
        }
    }, [closeCreateModal, createPricePeriod]);

    const handleTabChange = useCallback((nextTab) => {
        const normalizedTab = normalizeWorkbenchTab(nextTab);
        const nextSearchParams = new URLSearchParams(searchParams);

        if (normalizedTab === FUEL_PRICE_WORKBENCH_TABS.WORKBENCH) {
            nextSearchParams.delete(FUEL_PRICE_WORKBENCH_TAB_QUERY_KEY);
        } else {
            nextSearchParams.set(FUEL_PRICE_WORKBENCH_TAB_QUERY_KEY, normalizedTab);
        }

        setActiveTab(normalizedTab);
        setSearchParams(nextSearchParams, { replace: true });
    }, [searchParams, setActiveTab, setSearchParams]);

    return (
        <div>
            <FuelPriceWorkbenchHeader />

            <Tabs
                activeKey={activeTab}
                onChange={handleTabChange}
                items={[
                    {
                        key: FUEL_PRICE_WORKBENCH_TABS.WORKBENCH,
                        label: "周期确认工作台",
                        children: (
                            <PricePeriodWorkbenchPanel
                                stats={stats}
                                filters={filters}
                                vendors={vendors}
                                statusOptions={statusOptions}
                                periods={periods}
                                loading={loading}
                                pagination={pagination}
                                onFilterChange={handleFilterChange}
                                onSearch={searchPeriods}
                                onReset={resetFilters}
                                onCreate={openCreateModal}
                                onTableChange={handleTableChange}
                                onViewDetail={handleViewDetail}
                                onOpenOurPrice={handleOpenOurPrice}
                                onOpenSupplierQuote={handleOpenSupplierQuote}
                                onConfirm={confirmPricePeriod}
                                onDispute={handleOpenDispute}
                                onDelete={deletePricePeriod}
                                onOpenComparison={handleOpenComparison}
                            />
                        ),
                    },
                    {
                        key: FUEL_PRICE_WORKBENCH_TABS.LOGS,
                        label: "周期审计历史",
                        children: (
                            <OperationLogsTable
                                logs={logs}
                                loading={logsLoading}
                                pagination={logsPagination}
                                onRefresh={() => loadWorkbenchLogs(1)}
                                onChange={(nextPagination) => loadWorkbenchLogs(nextPagination.current)}
                            />
                        ),
                    },
                    {
                        key: FUEL_PRICE_WORKBENCH_TABS.MARKET_REFERENCE,
                        label: "市场参考价",
                        children: <MarketReferenceWorkbenchPanel />,
                    },
                ]}
            />

            <FuelPriceWorkbenchOverlays
                createModalOpen={createModalOpen}
                createLoading={createLoading}
                vendors={vendors}
                onCloseCreate={closeCreateModal}
                onCreate={handleCreate}
                priceInputState={priceInputState}
                priceInputPeriod={selectedPriceInputPeriod}
                priceInputConfig={priceInputConfig}
                priceInputLoading={priceInputLoading}
                onClosePriceInput={closePriceInput}
                onSubmitPriceInput={handleSubmitPriceInput}
                disputeState={disputeState}
                disputePeriod={selectedDisputePeriod}
                disputeLoading={disputeLoading}
                onCloseDispute={closeDispute}
                onSubmitDispute={handleSubmitDispute}
                comparisonState={comparisonState}
                comparisonPeriod={selectedComparisonPeriod}
                onCloseComparison={closeComparison}
                onViewDetail={handleViewDetail}
                onOpenOurPrice={handleOpenOurPrice}
                onOpenSupplierQuote={handleOpenSupplierQuote}
                onConfirm={confirmPricePeriod}
                onDispute={handleOpenDispute}
                detailState={detailState}
                detailPeriod={detailPeriod}
                detailLoading={detailLoading}
                detailLogs={detailLogs}
                detailLogsLoading={detailLogsLoading}
                onCloseDetail={handleCloseDetail}
            />
        </div>
    );
};

export default FuelPriceWorkbenchPage;
